// All requests use credentials:"include" so the session cookie is sent
// cross-origin during local dev.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";


import { BASE_URL } from "./api";

const API = `${BASE_URL}api/v1`;


export interface AuthConfig {
  enabled: boolean;
  google_client_id: string;
  allowed_email_domain: string;
  nms_auth: boolean;
}

export interface AuthUser {
  email: string;
  name?: string;
}


async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${API}${path}`, { credentials: "include", ...init });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`${r.status} ${r.statusText}: ${text || path}`);
  }
  return r.json() as Promise<T>;
}


export function useAuthConfig() {
  return useQuery({
    queryKey: ["auth", "config"],
    queryFn: () => fetchJson<AuthConfig>("/auth/config"),
    staleTime: Infinity,
  });
}


export function useCurrentUser() {
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      // 401 -> null (not signed in) instead of throwing, so AuthGate can render cleanly.
      const r = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (r.status === 401) return null;
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return r.json() as Promise<AuthUser>;
    },
    staleTime: 60 * 1000,
    retry: false,
  });
}


export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (credential: string) =>
      fetchJson<AuthUser>("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential }),
      }),
    onSuccess: (user) => {
      // Prime /me so AuthGate flips immediately without another round-trip.
      qc.setQueryData(["auth", "me"], user);
    },
  });
}


export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      }),
    onSuccess: () => {
      qc.setQueryData(["auth", "me"], null);
      // Drop data-bearing caches so the next sign-in can't see the previous user's data.
      qc.removeQueries({ queryKey: ["variants"] });
      qc.removeQueries({ queryKey: ["topology"] });
      qc.removeQueries({ queryKey: ["catalog"] });
    },
  });
}


// The GIS script in index.html attaches `window.google.accounts.id`.
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: {
            client_id: string;
            callback: (resp: { credential: string }) => void;
            hd?: string;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (parent: HTMLElement, options: {
            theme?: "outline" | "filled_blue" | "filled_black";
            size?: "small" | "medium" | "large";
            text?: "signin_with" | "signup_with" | "continue_with" | "signin";
            shape?: "rectangular" | "pill" | "circle" | "square";
            width?: number;
          }) => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}


export function gisAvailable(): boolean {
  return typeof window !== "undefined"
      && Boolean(window.google?.accounts?.id);
}
